import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import { LoadResourcePlaceList } from '@/javascript/domain/usecases'
import { FilterButton } from '@/javascript/presentation/components/buttons'
import { ResourcePlacesInput, SearchInput } from '@/javascript/presentation/components/inputs'
import { legacyHandleAddMemberModal } from '@/javascript/presentation/pages/account-members/list/helpers'
import { redirectWithQueryParams } from '@/javascript/presentation/helpers/redirect-with-query-params'
import HelpPopover from '@/javascript/presentation/pages/account-members/list/help-popover'
import InputData from '@/javascript/presentation/view-models/input-data'

type Props = {
  hasAddMemberPermission: boolean
  fetchResourcePlaces: LoadResourcePlaceList
  fetchResourcePlaceByIds: LoadResourcePlaceList
}

const Header: React.FC<Props> = ({ hasAddMemberPermission, fetchResourcePlaces, fetchResourcePlaceByIds }) => {
  const retrieveSearchParams = new URLSearchParams(window.location.search)

  const [searchText, setSearchText] = useState(retrieveSearchParams.get('q') || '')
  const [resourcePlaces, setResourcePlaces] = useState([])

  const fetchData = async (search: string): Promise<InputData[]> => {
    const result = await fetchResourcePlaces.load({ searchText: search })
    return result.map((place) => ({
      value: place.id.toString(),
      label: place.name
    }))
  }

  useEffect(() => {
    const fetchInitialResourcePlaces = async () => {
      const resourcePlaceIds = retrieveSearchParams
        .getAll('account_member[resource_place_ids][]')
        .map(id => parseInt(id))

      if (resourcePlaceIds.length === 0) return

      const result = await fetchResourcePlaces.load()
      const found = result.filter(rp =>
        resourcePlaceIds.includes(rp.id)
      )

      const foundIds = found.map(rp => rp.id)
      const missingIds = resourcePlaceIds.filter(
        id => !foundIds.includes(id)
      )

      let missing: any[] = []

      if (missingIds.length > 0)
        missing = await fetchResourcePlaceByIds.load({ ids: missingIds, withoutPagination: true })

      const all = [...found, ...missing]
      const inputData = all.map(rp => ({
        value: rp.id.toString(),
        label: rp.name
      }))

      setResourcePlaces(inputData)
    }

    fetchInitialResourcePlaces()
  }, [])

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant='h3'>Equipe <HelpPopover /></Typography>
        {hasAddMemberPermission &&
          <Box sx={{ display: 'flex', alignItems: 'center', verticalAlign: 'middle', gap: '1.5rem' }}>
            <Button variant="contained" onClick={legacyHandleAddMemberModal} size="medium">
              Adicionar Usuário
            </Button>
          </Box>
        }
      </Box>
      <Divider sx={{ m: '2rem 0' }} />
      <Typography variant="h6" color={'text.secondary'}>Filtros</Typography>
      <Box sx={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        <ResourcePlacesInput
          resourcePlaces={resourcePlaces}
          setResourcePlaces={setResourcePlaces}
          fetchResourcePlaces={(search) => fetchData(search)}
        />
        <SearchInput value={searchText} setValue={setSearchText} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', verticalAlign: 'middle', gap: '1.5rem' }}>
        <FilterButton
          variant="apply"
          onClick={() => redirectWithQueryParams(searchText, resourcePlaces.map(rp => parseInt(rp.value)))}
        />
        <FilterButton
          variant="clear"
          disabled={!searchText && resourcePlaces.length === 0}
          onClick={() => {
            setSearchText(''), setResourcePlaces([])
          }}
        />
      </Box>
    </>
  )
}

export default Header
